import {
  AuthnLevel,
  BaJwt,
  IdleSessionExpirationWarnEvent,
  JwtPayload,
  MaxSessionExpirationWarnEvent,
  OiamEvents,
} from '@oiambk/oiam-oauth-types';
import log from 'loglevel';
import { AuthnLevelTimes } from '../model/authnLevelTimes';
import { OauthStates, OiamCookie } from '../model/constants';
import { SessionCheckResult } from '../model/necessary-login';
import { OiamSessionCookie } from '../model/oiamSessionCookie';
import { calcProfileHash } from './acr-fns';
import { FeatureToggle } from './feature-toggle';
import { OauthEventService } from './oauth-event-service';
import { StoreService } from './store-service';

export type SessionCheckResultOrNext = SessionCheckResult | 'next';

export class SessionService {
  private authnLevelTimes: AuthnLevelTimes;
  private _timer = null;
  private bajwt: BaJwt;
  private ACCESS_TOKEN_EXPIRING_TRESHOLD = 30000;
  // time for token refresh (bajwt.exp - ACCESS_TOKEN_EXPIRING_TRESHOLD => refresh)
  private ACCESS_TOKEN_EXPIRING_TIME: number;

  private idleWarningDispatched: boolean = false;

  constructor() {
    // bind function for event listener add/remove
    this.handleUserAction = this.handleUserAction.bind(this);
  }

  /**
   * With the token store per client on duplicated tabs, the token of the tab at visibility change might not be valid
   * any more. This happens, if there was a token refresh in another tab with the same clientid. We have to resend
   * the updated token to the eService in such a case.
   * Compare the token of the session service (possible old one) with the one got from token storage (current one).
   * Return true if jwts are different (we have to resend the current token).
   */
  haveToResendTokenOnActivating(baJwt: BaJwt): boolean {
    return !!(this.bajwt?.payload.iat !== baJwt?.payload.iat);
  }

  /**
   * Check if there is an active and valid user session
   * or if there are actions necessary to get into a valid session state.
   */
  checkForValidSession(baJwt: BaJwt | undefined, parsedSessionCookie: OiamSessionCookie): SessionCheckResult {
    const sessionid = StoreService.checkForOiamSessionIdCookie();
    let result: SessionCheckResultOrNext;

    // prevent double token renew in timer (see initTimer)
    this.bajwt = undefined;

    // oiamsession cookie is older than the idle time (invalid stale cookie)
    // this can happen if the browser is long open on non BA eService or the browser restore function is used
    result = this.checkForStaleData(parsedSessionCookie);
    log.debug('oiam-oauth-wc: checkForStaleData', result);

    // oiamsession cookie prechecks: logged out after idle/max session timeout
    result = this.checkLoggedoutAfterIdleMaxTimeout(result, parsedSessionCookie);
    log.debug('oiam-oauth-wc: checkLoggedoutAfterIdleMaxTimeout', result);

    result = this.checkLoggedOut(result, sessionid, parsedSessionCookie);
    log.debug('oiam-oauth-wc: checkLoggedOut', result);

    // check if there is no session
    result = this.checkStaleJWT(result, sessionid, baJwt?.raw);
    log.debug('oiam-oauth-wc: checkExternalLoggedOut', result);

    // check if login/relogin is necessary
    result = this.checkValidLogin(result, sessionid, baJwt);
    log.debug('oiam-oauth-wc: checkValidLogin', result);

    // check for stepup
    result = this.checkForStepup(result, parsedSessionCookie, baJwt);
    log.debug('oiam-oauth-wc: checkForStepup', result);

    // restart session timer and user action handler if correctly logged in
    // and return the result
    return this.restartSessionOrReturnAction(result, baJwt, parsedSessionCookie?.authnlevel);
  }

  /**
   * Start the local user session after receiving the tokens from IAM
   */
  startSession(jwt: BaJwt) {
    this.setJwt(jwt);
    const idlestart = Date.now();
    // store profile if exists
    const profile = SessionService.jwtToSessionCookieParts(jwt.payload, idlestart);
    StoreService.storeOiamSessionCookieKeepAdditionalInfo(profile);

    this.startSessionTimer(this.bajwt.payload['authn-level']);
    this.startUserActionHandler();
  }

  /**
   * Stop the local user session at logout
   */
  stopSession(keepRetryInvocation: boolean) {
    this.bajwt = undefined;
    this.stopTimer();
    StoreService.deletePreviousInvocation();
    if (!keepRetryInvocation) {
      StoreService.deleteRetryInvocation();
    }

    document.removeEventListener('keypress', this.handleUserAction);
    document.removeEventListener('mouseup', this.handleUserAction);
  }

  /**
   * Set new jwt and authn-level always if successful received the token from IAM
   */
  setJwt(jwt: BaJwt) {
    this.bajwt = jwt;
    const level = jwt.payload['authn-level'];
    if (!this.authnLevelTimes || level !== this.authnLevelTimes.authnLevel) {
      this.authnLevelTimes = new AuthnLevelTimes(level);
    }
    this.ACCESS_TOKEN_EXPIRING_TIME = this.bajwt.expirationDate * 1000 - this.ACCESS_TOKEN_EXPIRING_TRESHOLD;
  }

  /**
   * Check if idle session time is expired
   */
  isIdleSessionExpired(parsedSessionCookie?: OiamSessionCookie) {
    const sessionid = StoreService.checkForOiamSessionIdCookie();
    if (sessionid) {
      const cookiesession: OiamSessionCookie = parsedSessionCookie
        ? parsedSessionCookie
        : StoreService.getOiamSessionCookie();
      // no oiam session cookie? Technically, we can't determine that the session has exired.
      // In practice, other parts of the code detect this and trigger a relogin.
      if (!cookiesession) {
        return false;
      }
      // no authnLevelTimes set? Set it from cookie
      if (!this.authnLevelTimes) {
        this.authnLevelTimes = new AuthnLevelTimes(cookiesession.authnlevel);
      }
      // get idlestart from oiamsession or BackendAccessTS cookie
      const idlestart = this.getEffectiveIdleStart(cookiesession);
      const time = Date.now();
      const idletimeout = idlestart + this.authnLevelTimes.sessionTime.idletime;

      log.debug(
        'oiam-oauth-wc: isIdleSessionExpired',
        'idletimeout',
        new Date(idletimeout).toLocaleTimeString(),
        'now',
        new Date(time).toLocaleTimeString(),
        this.authnLevelTimes.authnLevel,
      );
      return time >= idletimeout;
    }
    return false;
  }

  /**
   * Check if max session time is expired
   */
  isMaxSessionExpired() {
    const maxtimeout = this.getSessionTimeout();
    if (!maxtimeout) {
      return false;
    }
    const time = Date.now();
    log.debug(
      'oiam-oauth-wc: isMaxSessionExpired',
      'maxtimeout',
      new Date(maxtimeout).toLocaleTimeString(),
      'now',
      new Date(time).toLocaleTimeString(),
      this.authnLevelTimes.authnLevel,
    );
    return time >= maxtimeout;
  }

  /**
   * Returns the session-timeout (in milliseconds) in regards of the authnLevel of the user
   * (if there is a JWT present)
   */
  public getSessionTimeout(): number | undefined {
    if (this.bajwt?.payload) {
      return this.bajwt.payload.auth_time * 1_000 + this.authnLevelTimes.sessionTime.maxsessiontime;
    }
    return undefined;
  }

  /**
   * Sends an 'oiamSessionEvent' including the session-end-timestamp and a given AuthnLevel
   */
  public async sendSessionEvent() {
    const sessionEnd = this.getSessionTimeout();
    if (sessionEnd !== undefined) {
      const detail = {
        sessionEnd,
        authnLevel: this.authnLevelTimes.authnLevel,
      };
      OauthEventService.doDispatchEvent(OiamEvents.SESSION, detail);
    }
  }

  /**
   * Creates an MaxSessionExpirationWarnEvent. Event Signature is depending on the FeatureToggle.
   *
   * @param msUntilSessionEnd
   * Time until the session will expire.
   * E.g. 30 minutes (accompanied with nextWarningMsBeforeSessionEnd=600_000) or 5 minutes
   * @param nextWarningMsBeforeSessionEnd
   * Informational value when the next session end warning will be dispatched.
   * E.g. 5 minutes for the 30 minutes event.
   *
   */
  sendMaxSessionExpireWarnEvent(msUntilSessionEnd: number, nextWarningMsBeforeSessionEnd?: number) {
    const event: MaxSessionExpirationWarnEvent = {
      msUntilSessionEnd,
      nextWarningMsBeforeSessionEnd,
    };
    OauthEventService.doDispatchEvent(OiamEvents.MAXSESSIONEXPIRATIONWARNING, event);
  }

  /**
   * Creates an IdleSessionExpireWarnEvent. Event Signature is depending on the FeatureToggle.
   *
   * @param msUntilIdleTimeout
   * ms until forced logoff if no further interaction occurs
   */
  sendIdleSessionExpireWarnEvent(msUntilIdleTimeout: number) {
    if (FeatureToggle.supportDeprecatedExpireEvents()) {
      OauthEventService.doDispatchEvent(OiamEvents.IDLESESSIONEXPIREWARNING, msUntilIdleTimeout);
    } else {
      const event: IdleSessionExpirationWarnEvent = {
        msUntilIdleTimeout,
      };
      OauthEventService.doDispatchEvent(OiamEvents.IDLESESSIONEXPIRATIONWARNING, event);
    }
  }

  public hasSessionBeenTerminated() {
    const sessionid = StoreService.checkForOiamSessionIdCookie();
    // OAG deletes this cookie when detecting a successful logout.
    // Its absence is a valid trigger to consider us as logged-out.
    return !sessionid;
  }

  // ### private methods ###############################################################

  /**
   * Check if there was a logout after idle or max session timeout to send the according expired event
   *
   * @param cookiesession
   * @private
   */
  private checkLoggedoutAfterIdleMaxTimeout(
    oldaction: SessionCheckResultOrNext,
    cookiesession?: OiamSessionCookie,
  ): SessionCheckResultOrNext {
    if (oldaction !== 'next' || !cookiesession) {
      return oldaction;
    }

    if (cookiesession.additionalinfo && cookiesession.additionalinfo === 'loggedout-idle-timeout') {
      StoreService.deleteOiamCookie(OiamCookie.SESSIONCOOKIE);
      return 'other-idle-timeout';
    }
    if (cookiesession.additionalinfo && cookiesession.additionalinfo === 'loggedout-max-timeout') {
      StoreService.deleteOiamCookie(OiamCookie.SESSIONCOOKIE);
      return 'other-max-timeout';
    }

    if (this.isMaxSessionExpired()) {
      return 'self-max-timeout';
    }
    return 'next';
  }

  /**
   * When logging out, we clear sessionStorage, but not oiamsessioncookie. OAG deletes BA-SessionId cookie.
   * When seeing its absence, we have to complete the cleanup by deleting oiamsessioncookie and sessionStorage
   * in case logoutredirecturi is on another origin.
   */
  private checkLoggedOut(
    oldaction: SessionCheckResultOrNext,
    sessionid: string | undefined,
    cookiesession: OiamSessionCookie | undefined,
  ): SessionCheckResultOrNext {
    if (oldaction !== 'next' || sessionid || !cookiesession) {
      return oldaction;
    }
    return 'other-logged-out';
  }

  /**
   * Check for no session and stale user (jwt)
   */
  private checkStaleJWT(oldresult: SessionCheckResultOrNext, sessionid: string, jwt: string): SessionCheckResultOrNext {
    if (oldresult !== 'next' || sessionid) {
      return oldresult;
    }

    // external logged out
    if (jwt) {
      return 'stale-user';
    }
    return 'no-session';
  }

  /**
   * Check if the idle session timed out at a non BA tab or the browser was restored with an old logged-in state
   * Delete the stale cookies in that case
   * Only execute the check if there is no previous result
   */
  private checkForStaleData(cookiesession: OiamSessionCookie): SessionCheckResultOrNext {
    if (this.isStaleOiamCookie(cookiesession)) {
      return this.cleanupStaleData();
    }
    return 'next';
  }

  private isStaleOiamCookie(cookiesession: OiamSessionCookie): boolean {
    if (cookiesession) {
      if (!cookiesession.authnlevel) {
        log.info('oiam-oauth-wc: old oiamsession cookie without authnLevel, clean up and start anew');
        return true;
      }
      // return after idle/max-session-timeout
      if (cookiesession.additionalinfo) {
        return false;
      }
      const authnLevelTimes = new AuthnLevelTimes(cookiesession.authnlevel);
      const earliestPossibleIdlestart = Date.now() - authnLevelTimes.sessionTime.idletime;
      // get idlestart from oiamsession or BackendAccessTS cookie
      const idlestart = this.getEffectiveIdleStart(cookiesession);
      if (idlestart > 0 && idlestart < earliestPossibleIdlestart) {
        log.debug(
          'oiam-oauth-wc: old oiamsession cookie with idle time already timed out!',
          idlestart,
          '<',
          earliestPossibleIdlestart,
        );
        return true;
      }
    }
    return false;
  }

  /**
   * cleanup stale BA-SessionId and oiamsession cookie
   */
  private cleanupStaleData(): 'stale-cookie' {
    StoreService.deleteOiamCookie(OiamCookie.SESSIONID);
    StoreService.deleteOiamCookie(OiamCookie.SESSIONCOOKIE);
    // cleanup stale sessionStorage
    return 'stale-cookie';
  }

  /**
   * Check for external logged in, different client id or different sessionid
   * Only execute the check if there is no previous action
   */
  private checkValidLogin(oldaction: SessionCheckResultOrNext, sessionid: string, baJwt: BaJwt) {
    if (oldaction === 'next') {
      // not logged in => no login
      if (!sessionid) {
        return 'next';
      }
      // sessionid, but no user
      if (!baJwt?.payload) {
        return 'missing-user';
      }
      // different sessionid -> logout + login other tab
      if (baJwt.payload.sid !== sessionid) {
        return 'session-id-changed';
      }
    }
    return oldaction;
  }

  /**
   * Check existing local user session against the values in the oiamsession cookie and decides
   * necessary actions to perform to get a valid session state.
   * Only execute the check if there is no previous action.
   */
  private checkForStepup(
    oldaction: SessionCheckResultOrNext,
    cookiesession: OiamSessionCookie,
    baJwt: BaJwt,
  ): SessionCheckResultOrNext {
    if (oldaction !== 'next') {
      return oldaction;
    }

    if (cookiesession) {
      if (!this.canIgnoreOwnOiamsessionCookie(cookiesession)) {
        // If the user has authenticated himself in the meantime, our refresh token can no longer be used.
        // We have to relogin to get a fresh one.
        const stepupDetected = baJwt.payload.auth_time < cookiesession.maxstart;
        if (stepupDetected) {
          // If this is the same as before, nothing bad happens.
          this.authnLevelTimes = new AuthnLevelTimes(cookiesession.authnlevel);
          return 'session-start-changed';
        }
      }
      return 'next';
    }
    // error case: no oiamsession cookie -> there must be something terrible wrong -> renew session at IAM
    return 'missing-oiamsession-cookie';
  }

  /**
   * Restart session timer and user action handler
   * Only execute if there is no previous action
   */
  private restartSessionOrReturnAction(
    oldaction: SessionCheckResultOrNext,
    baJwt: BaJwt,
    authnlevel: AuthnLevel,
  ): SessionCheckResult {
    if (oldaction === 'next') {
      // we are logged in: restart session timer and user action handler etc.
      this.setJwt(baJwt);
      this.startSessionTimer(authnlevel);
      this.startUserActionHandler();
      return 'session-valid';
    }
    return oldaction;
  }

  private setIdleStart() {
    const sessionid = StoreService.checkForOiamSessionIdCookie();
    if (sessionid) {
      this.idleWarningDispatched = false;

      // update session cookie for other tabs
      // store with current profile from cookie (else it would be deleted)
      StoreService.updateOiamSessionCookie({ idlestart: Date.now() });
    }
  }

  /**
   * Rekursiver Timer, der jede Sekunde einen Tick hat in dem die einzelnen Timeout entprechend Vertrauensniveau
   * geprüft werden.
   */
  private initTimer() {
    this.stopTimer(); // allow only one instance
    this._timer = setTimeout(() => {
      const now = Date.now();
      this.check(now);
      this.initTimer();
    }, 1000);
  }

  private check(datetime: number) {
    const parsedSessionCookie: OiamSessionCookie | undefined = StoreService.getOiamSessionCookie();
    // keine valide session mehr
    if (!parsedSessionCookie) {
      return;
    }
    // get idlestart from oiamsession or BackendAccessTS cookie
    let idlestart = this.getEffectiveIdleStart(parsedSessionCookie);

    // check external logout or idle session renew
    // only for inactive tabs
    if (document.visibilityState !== 'visible') {
      // check sessionid cookie for external logout
      // ToDo: Überdenken, ist doppelt mit updateAfterVisibilityChange und kann eigentlich weg
      if (this.hasSessionBeenTerminated()) {
        window.self.dispatchEvent(
          new CustomEvent(OiamEvents.DOLOGOUT, { detail: { reason: OauthStates.LOGGEDOUTEXTERNAL } }),
        );
      }
    }

    const idletimeout = idlestart + this.authnLevelTimes.sessionTime.idletime;
    const idlewarntimeout = idletimeout - this.authnLevelTimes.sessionTime.idlewarntime;
    const maxtimeout = this.getSessionTimeout();

    // idle warn
    // only for active tab
    if (datetime >= idlewarntimeout && document.visibilityState === 'visible') {
      this.idleWarning();
    }

    // idle session timeout
    // check independent of visibility state of the tab
    if (datetime >= idletimeout) {
      log.debug(
        'Idle Timer Tick',
        'now=',
        new Date(datetime).toLocaleString(),
        'idletimeout=',
        new Date(idletimeout).toLocaleString(),
      );

      // handled by state-service
      window.self.dispatchEvent(new CustomEvent(OiamEvents.DOLOGOUT, { detail: { reason: OauthStates.IDLETIMEDOUT } }));
      return;
    }

    // check max session timeout or necessary token refresh
    // only for active tab
    if (document.visibilityState === 'visible') {
      this.checkMaxSessionTimeoutWarn(maxtimeout, datetime);

      // max session timeout ?
      if (datetime >= maxtimeout) {
        log.debug(
          'Max Session Timer Tick',
          'now=',
          new Date(datetime).toLocaleString(),
          'maxtimeout=',
          new Date(maxtimeout).toLocaleString(),
        );

        // handled by state-service
        window.self.dispatchEvent(
          new CustomEvent(OiamEvents.DOLOGOUT, { detail: { reason: OauthStates.MAXTIMEDOUT } }),
        );
        return;
      }

      // check token and init token refresh if necessary
      if (this.bajwt) {
        if (this.shouldRefreshToken(datetime, idlestart)) {
          log.debug(
            'oiam-oauth-wc: token expired -> send renew token event. Now:',
            new Date(datetime).toLocaleTimeString(),
            'idlestart:',
            new Date(idlestart).toLocaleTimeString(),
            'iat:',
            new Date(this.bajwt.payload.iat * 1000).toLocaleTimeString(),
          );
          window.self.dispatchEvent(new CustomEvent(OiamEvents.SENDTOKEN, { detail: { forcerenew: true } }));
        }
      }
    }
  }

  private startSessionTimer(authnlevel: AuthnLevel) {
    this.authnLevelTimes = new AuthnLevelTimes(authnlevel);
    this.initTimer();
  }

  private startUserActionHandler() {
    // remove existing handlers to prevent douple handling
    document.removeEventListener('keypress', this.handleUserAction);
    document.removeEventListener('mouseup', this.handleUserAction);

    // handle keyboard and click events as user actions
    document.addEventListener('keypress', this.handleUserAction);
    document.addEventListener('mouseup', this.handleUserAction);
  }

  private checkMaxSessionTimeoutWarn(maxtimeout: number, datetime: number) {
    const remainingUntilTimeout = maxtimeout - datetime;

    // check 5min timeout
    if (remainingUntilTimeout <= 300000) {
      this.maxSessionExpireWarn5(remainingUntilTimeout);
      // do not send first warn message if already under 5min
      return;
    }

    // check 30min timeout -> first warn message
    if (this.authnLevelTimes.authnLevel !== 'STORK-QAA-Level-4' && remainingUntilTimeout <= 1800000) {
      this.maxSessionExpireWarn30(remainingUntilTimeout);
    }
  }

  private shouldRefreshToken(datetime: number, idlestart: number): boolean {
    if (datetime <= this.ACCESS_TOKEN_EXPIRING_TIME) {
      return false;
    }
    if (idlestart > this.bajwt.payload.iat * 1000) {
      return true;
    }
    return false;
  }

  private idleWarning() {
    // idle warning only once
    if (!this.idleWarningDispatched) {
      this.sendIdleSessionExpireWarnEvent(this.authnLevelTimes.sessionTime.idlewarntime);
      this.idleWarningDispatched = true;
      log.debug('Idle Warn Timer Tick');
    }
  }

  private maxSessionExpireWarn30(remainingTime: number) {
    // session expire warning only once
    const oiamSessionCookie: OiamSessionCookie = StoreService.getOiamSessionCookie();
    if (
      !oiamSessionCookie ||
      !oiamSessionCookie.additionalinfo ||
      oiamSessionCookie.additionalinfo !== 'max-warn-30-sent'
    ) {
      this.sendMaxSessionExpireWarnEvent(remainingTime, 5 * 60 * 1000);
      StoreService.setAdditionalInfoInOiamSessionCookie('max-warn-30-sent');
      log.debug('Session Expire Warn Timer Tick (30)');
    }
  }

  private maxSessionExpireWarn5(remainingTime: number) {
    // session expire warning only once
    const oiamSessionCookie: OiamSessionCookie = StoreService.getOiamSessionCookie();
    if (
      !oiamSessionCookie ||
      !oiamSessionCookie.additionalinfo ||
      oiamSessionCookie.additionalinfo !== 'max-warn-5-sent'
    ) {
      this.sendMaxSessionExpireWarnEvent(remainingTime);
      StoreService.setAdditionalInfoInOiamSessionCookie('max-warn-5-sent');
      log.debug('Session Expire Warn Timer Tick (5)');
    }
  }

  /**
   * Compares session cookies' idlestart time with the last backend access at OAG.
   *
   * @returns: newest idle start time
   */
  private getEffectiveIdleStart(cookiesession: OiamSessionCookie) {
    const oagAccess = StoreService.getBackendAccessTS() * 1000;
    let otherOidcWcActivity = 0;
    if (cookiesession) {
      otherOidcWcActivity = cookiesession.idlestart;
    }
    return Math.max(otherOidcWcActivity, oagAccess);
  }

  private stopTimer() {
    if (this._timer) {
      clearTimeout(this._timer);
      this._timer = null;
    }
  }

  private handleUserAction() {
    this.setIdleStart();
  }

  private canIgnoreOwnOiamsessionCookie(oiamsessioncookie: OiamSessionCookie) {
    return (
      StoreService.isSameInstance(oiamsessioncookie.instance) &&
      oiamsessioncookie.clientid === StoreService.getClientId()
    );
  }

  private static jwtToSessionCookieParts(payload: JwtPayload, idlestart: number) {
    const profileHash = calcProfileHash(payload);
    const profile = {
      ...(payload['acting-on-behalf-of']?.length > 0 && {
        actingOnBehalfOf: payload['acting-on-behalf-of'],
      }),
      ...(payload['acting-type'] && { actingType: payload['acting-type'] }),
      maxstart: payload.auth_time,
      idlestart,
      authnlevel: payload['authn-level'],
      profileHash,
    };
    return profile;
  }
}
